import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { CheckCircle, Package, Loader2, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import confetti from 'canvas-confetti';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const OrderSuccess = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const paymentId = searchParams.get('payment_id');
  const status = searchParams.get('status');

  useEffect(() => {
    // Confetti!
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });

    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/orders/${orderId}`,
        { withCredentials: true }
      );
      setOrder(response.data);
    } catch (error) {
      console.error('Erro ao carregar pedido:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-900 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-900 to-slate-900">
      <Navbar />
      
      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-emerald-500/30 p-8 text-center">
          {/* Success Icon */}
          <div className="w-20 h-20 bg-gradient-to-r from-emerald-500 to-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>

          <h1 className="text-3xl font-bold text-white mb-4" data-testid="order-success-title">
            Pedido Confirmado!
          </h1>

          <p className="text-slate-300 text-lg mb-6">
            Seu pagamento foi aprovado com sucesso. O vendedor foi notificado e em breve entrará em contato.
          </p>

          {/* Order Details */}
          <div className="bg-slate-700/50 rounded-lg p-4 mb-6 text-left">
            <h3 className="text-white font-medium mb-3">Detalhes do Pedido</h3>
            <div className="space-y-2 text-sm">
              <p className="text-slate-400">
                Pedido: <span className="text-white font-mono">{orderId}</span>
              </p>
              {paymentId && (
                <p className="text-slate-400">
                  Pagamento: <span className="text-white font-mono">{paymentId}</span>
                </p>
              )}
              {order && (
                <>
                  <p className="text-slate-400">
                    Total: <span className="text-emerald-400 font-bold">R$ {order.total?.toFixed(2)}</span>
                  </p>
                  <p className="text-slate-400">
                    Itens: <span className="text-white">{order.items?.length || 0}</span>
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Items */}
          {order?.items && order.items.length > 0 && (
            <div className="bg-slate-700/30 rounded-lg p-4 mb-6">
              <h4 className="text-white font-medium mb-3 text-left">Itens Comprados</h4>
              <div className="space-y-2">
                {order.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center text-sm">
                    <span className="text-slate-300">{item.name} x{item.quantity}</span>
                    <span className="text-white">R$ {item.total_price?.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={() => navigate('/orders/my')}
              className="bg-gradient-to-r from-emerald-500 to-green-500 hover:opacity-90"
              data-testid="view-orders-btn"
            >
              <Package className="w-4 h-4 mr-2" />
              Ver Meus Pedidos
            </Button>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="border-slate-600 text-white hover:bg-slate-700"
            >
              <ArrowRight className="w-4 h-4 mr-2" />
              Continuar Comprando
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
